import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from "@/lib/seo";
import { useI18n, SUPPORTED_LANGS } from "@/lib/i18n";

/**
 * Centralized SEO component.
 * Renders <title>, meta description, canonical, OpenGraph, Twitter Card and
 * any JSON-LD blocks provided by the page. Organization / LocalBusiness JSON-LD
 * is injected once globally in /public/index.html with stable @id so it is not
 * duplicated on every page. Per-page JSON-LD (Service, BreadcrumbList, FAQ,
 * JobPosting…) is passed via the `jsonLd` prop.
 *
 * No hreflang is emitted: French / German / English share the same URL with
 * client-side language switching, so there is no distinct localised URL to
 * point to. The day we ship /de/* and /en/* prefixes we will re-enable it.
 */
export default function Seo({
  title,
  description,
  path = "/",
  ogImage = DEFAULT_OG_IMAGE,
  noindex = false,
  jsonLd = null,
}) {
  const { lang } = useI18n();
  // Build a fully-qualified URL for a given language, normalised the same way
  // as the inline canonical script (strip trailing slash except root).
  const buildUrl = (l) => {
    const pre = l === "fr" ? "" : `/${l}`;
    let p = pre + (path === "/" ? "/" : path);
    if (p.length > 1) p = p.replace(/\/+$/, "");
    return `${SITE_URL}${p}`;
  };
  const url = buildUrl(lang);
  const fullTitle = title ? (title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`) : SITE_NAME;
  const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const hreflangCode = { fr: "fr-CH", de: "de-CH", en: "en" };
  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <meta
        name="robots"
        content={noindex
          ? "noindex,follow"
          : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"}
      />
      {/* hreflang alternates: FR at root, DE under /de, EN under /en, x-default=FR.
          Emitted only for indexable pages (every public page exists in all 3
          languages). Skipped on noindex/app pages. */}
      {!noindex && SUPPORTED_LANGS.map((l) => (
        <link key={l} rel="alternate" hrefLang={hreflangCode[l]} href={buildUrl(l)} />
      ))}
      {!noindex && <link rel="alternate" hrefLang="x-default" href={buildUrl("fr")} />}
      {/* <link rel="canonical"> is handled by an inline script in public/index.html
          that sets the canonical dynamically from window.location.pathname.
          This guarantees self-canonical URLs even when the SPA fallback serves
          /index.html for nested routes. Having Helmet add another <link rel="canonical">
          here would create a duplicate, so we leave it to the inline script. */}
      {/* OpenGraph */}
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="fr_CH" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />
      {/* Per-page JSON-LD blocks (Service, Breadcrumb, FAQ, JobPosting…) */}
      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>
      ))}
    </Helmet>
  );
}
